import { useEffect, useMemo, useState } from 'react';

const noopSorter = () => 0;

const resolveValue = (item, key) => {
  if (typeof key === 'function') return key(item);
  if (!key) return undefined;

  return key.split('.').reduce((accumulator, part) => accumulator?.[part], item);
};

export const useTableControls = (
  items = [],
  {
    initialSortKey = '',
    initialSortDirection = 'asc',
    initialPageSize = 10,
    initialSearchTerm = '',
    sorters = {},
    searchKeys = [],
    searchPredicate,
  } = {},
) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  const filteredItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;

    return items.filter((item) => {
      if (typeof searchPredicate === 'function') return searchPredicate(item, term);
      if (!searchKeys || searchKeys.length === 0) return true;

      return searchKeys.some((key) => {
        const value = resolveValue(item, key);

        if (Array.isArray(value)) {
          return value.some((entry) => String(entry ?? '').toLowerCase().includes(term));
        }

        return String(value ?? '').toLowerCase().includes(term);
      });
    });
  }, [items, searchKeys, searchPredicate, searchTerm]);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(filteredItems)) return [];
    const copied = [...filteredItems];

    if (!sortKey || typeof sorters[sortKey] !== 'function') return copied;

    return copied.sort((first, second) => {
      const comparator = sorters[sortKey] || noopSorter;
      const result = comparator(first, second);
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filteredItems, sortDirection, sortKey, sorters]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, sortKey, sortDirection, filteredItems, searchTerm]);

  const totalItems = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const paginatedItems = sortedItems.slice(start, end);

  const setPageSafe = (value) => {
    if (Number.isNaN(value)) return;
    const nextValue = Math.min(Math.max(value, 1), totalPages);
    setPage(nextValue);
  };

  const setPageSizeSafe = (value) => {
    if (Number.isNaN(value) || value <= 0) return;
    setPageSize(value);
  };

  const toggleDirection = () => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));

  return {
    page: currentPage,
    pageSize,
    sortKey,
    sortDirection,
    searchTerm,
    totalItems,
    totalPages,
    paginatedItems,
    setPage: setPageSafe,
    setPageSize: setPageSizeSafe,
    setSortKey,
    setSortDirection,
    setSearchTerm,
    toggleDirection,
  };
};

export default useTableControls;
